import { STAFF_DOMAIN, STAFF_ROLE_LABELS, can, emailDomain, type ApprovalRequest, type AuditEntry, type StaffAccess, type StaffRole } from '@titan/shared';
import { useState } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import { Loading, ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import {
  Button,
  Card,
  ChipSelector,
  ConfirmButton,
  EmptyState,
  InlineError,
  InlineInfo,
  ListContainer,
  ListHeader,
  ListRow,
  Pill,
  PillRow,
  PrimaryCell,
  RowMeta,
  RowTitle,
  SecondaryCell,
  TextInput,
} from '../../ui/index.js';
import { ApprovalsCard } from '../access/ApprovalsCard.js';
import { useApprovalNotice } from '../access/useNotice.js';
import { AuditList } from '../org/AuditList.js';
import { errorMessage, useLoad } from '../useLoad.js';
import styles from './Admin.module.css';

interface StaffResponse {
  staff: StaffAccess[];
  audit: AuditEntry[];
  approvals: ApprovalRequest[];
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Spec 07 DinoCloud team, as an access-grant list. Making a Platform Owner waits for another one. */
export function Staff() {
  const { api, me } = useSession();
  const { data, error, loading, reload } = useLoad<StaffResponse>('/admin/staff');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, check, clear } = useApprovalNotice();

  if (!me) return null;
  const canGrant = can(me.actor, 'staff') === 'full';
  const staff = data?.staff ?? [];
  const active = staff.filter((s) => s.status === 'Active');
  const owners = active.filter((s) => s.role === 'superadmin').length;
  const emailOk = emailDomain(email) === STAFF_DOMAIN;

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    clear();
    try {
      check(await work());
      await reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const grant = () =>
    run(async () => {
      const res = await api('/admin/staff', { method: 'POST', body: JSON.stringify({ email: email.trim(), name: name.trim(), role }) });
      setEmail('');
      setName('');
      setRole('staff');
      return res;
    });

  const patch = (s: StaffAccess, body: Partial<Pick<StaffAccess, 'status' | 'role'>>) =>
    run(() => api(`/admin/staff/${encodeURIComponent(s.email)}`, { method: 'PATCH', body: JSON.stringify(body) }));

  return (
    <>
      <ScreenHeader
        eyebrow="administration · dinocloud team"
        title="DinoCloud team"
        description={`Who at DinoCloud can enter the hub. Staff sign in with their @${STAFF_DOMAIN} Google account, and only once a Platform Owner has granted them access. Making someone a Platform Owner waits for another Platform Owner.`}
      />
      <ScreenBody>
        {(error || actionError) && <InlineError>{actionError ?? error}</InlineError>}
        {notice && <InlineInfo>{notice}</InlineInfo>}
        {loading && !data && <Loading />}
        {data && <ApprovalsCard approvals={data.approvals} actor={me.actor} users={[]} onChanged={reload} />}
        <ListContainer>
          <ListHeader title={`${active.length} people with access`} meta={`${owners} platform owner${owners === 1 ? '' : 's'} · every session behind SSO and MFA`} />
          {canGrant && (
            <div className={styles.inviteBar}>
              <div className={styles.inviteRow}>
                <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`name@${STAFF_DOMAIN}`} aria-label="Staff email" invalid={email.length > 0 && !emailOk} />
                <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" aria-label="Name" />
                <Button onClick={grant} disabled={busy || !emailOk || !name.trim()}>
                  {role === 'superadmin' ? 'Grant access and ask for approval' : 'Grant access'}
                </Button>
              </div>
              <ChipSelector<StaffRole>
                aria-label="Role"
                options={[
                  { value: 'staff', label: STAFF_ROLE_LABELS.staff },
                  { value: 'superadmin', label: STAFF_ROLE_LABELS.superadmin },
                ]}
                value={role}
                onChange={setRole}
              />
              {role === 'superadmin' && <span className={styles.reason}>Staff access is granted at once. The Platform Owner role waits for another Platform Owner to approve.</span>}
            </div>
          )}
          {!loading && staff.length === 0 && <EmptyState>Nobody has access yet.</EmptyState>}
          {staff.map((s) => (
            <ListRow key={s.email} center>
              <PrimaryCell>
                <RowTitle small>{s.name}</RowTitle>
                <RowMeta>{s.email}</RowMeta>
              </PrimaryCell>
              <SecondaryCell size="lg">
                <PillRow>
                  <Pill tone={s.role === 'superadmin' ? 'premier' : 'positive'}>{STAFF_ROLE_LABELS[s.role]}</Pill>
                  {s.status === 'Revoked' && <Pill tone="critical">Revoked</Pill>}
                </PillRow>
              </SecondaryCell>
              <SecondaryCell size="xl">
                <RowMeta>
                  Granted {when(s.grantedAt)} by {s.grantedBy}
                </RowMeta>
                <RowMeta>{s.last ? `Last sign-in ${when(s.last)}` : 'Has not signed in yet'}</RowMeta>
              </SecondaryCell>
              {canGrant && s.email !== me.actor.email && (
                <SecondaryCell size="lg">
                  <div className={styles.actionsCol}>
                    {s.status === 'Active' ? (
                      <ConfirmButton
                        label="Revoke"
                        tone="critical"
                        disabled={busy}
                        message={`${s.name} can no longer sign in to Titan. Their Google account is unaffected; you can restore access later.`}
                        confirmLabel="Revoke access"
                        onConfirm={() => patch(s, { status: 'Revoked' })}
                      />
                    ) : (
                      <Button variant="secondary" size="sm" disabled={busy} onClick={() => patch(s, { status: 'Active' })}>
                        Restore
                      </Button>
                    )}
                    {s.status === 'Active' && (
                      <Button variant="link" size="sm" disabled={busy} onClick={() => patch(s, { role: s.role === 'superadmin' ? 'staff' : 'superadmin' })}>
                        {s.role === 'superadmin' ? 'Make staff' : 'Make Platform Owner'}
                      </Button>
                    )}
                  </div>
                </SecondaryCell>
              )}
            </ListRow>
          ))}
        </ListContainer>

        <Card title="What each internal role can do" description="Every staff member can operate any tenant. Only Platform Owners change who is on this list and approve sensitive changes.">
          <div>
            <div className={styles.roleRow}>
              <span className={styles.roleName}>Platform Owner</span>
              <span className={styles.roleDesc}>Creates tenants, changes entitlements, manages DinoCloud staff, approves sensitive changes made by another Platform Owner</span>
              <span className={styles.roleScope}>Scope: full</span>
            </div>
            <div className={styles.roleRow}>
              <span className={styles.roleName}>DinoCloud staff</span>
              <span className={styles.roleDesc}>Creates tenants, changes entitlements, edits any tenant’s roles and groups, approves client-side changes, impersonates tenants. Cannot change the DinoCloud team.</span>
              <span className={styles.roleScope}>Scope: full</span>
            </div>
          </div>
        </Card>

        <AuditList audit={data?.audit ?? []} title="Team activity" />
      </ScreenBody>
    </>
  );
}
